import currency from "currency.js";
import { IConfirmationButton, Stat } from ".";
import {
  GetAccount200ResponsePositionsInner,
  GetRfqs200ResponseBlocksInner,
  GetRfqsBlockIdQuotes200ResponseAsksInner,
  InstrumentTypeResponse,
} from "../../codegen-api";
import { formatSizePrecision } from "../../utils/format";
import { SPACING } from "../../constants/design/spacing";
import ShareButton from "../Buttons/ShareButton";
import BackButton from "../Buttons/BackButton";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import { useGetAccount } from "../../hooks/api/account/useGetAccount";
import { RFQ } from "./RFQ";
import { StatWrapper } from "./style";
import { ClosePositionPageEnum } from "./ClosePositionModal.types";

type TranslateFn = (...args: any[]) => any;
type AccountData = ReturnType<typeof useGetAccount>["data"];

interface IBuildPageContentParams {
  page: ClosePositionPageEnum;
  position: GetAccount200ResponsePositionsInner | undefined;
  hasQuote: boolean;
  hasRequest: boolean;
  selectedQuote: GetRfqsBlockIdQuotes200ResponseAsksInner | undefined;
  t: TranslateFn;
}

export const buildPageContent = ({
  page,
  position,
  hasQuote,
  hasRequest,
  selectedQuote,
  t,
}: IBuildPageContentParams): JSX.Element | null => {
  switch (page) {
    case ClosePositionPageEnum.REQUEST_RFQ:
      return (
        <>
          <RFQ
            position={position!}
            hasQuote={hasQuote}
            hasRequest={hasRequest}
          />
          {selectedQuote ? (
            <StatWrapper>
              <Stat
                key={t("contracts")}
                title={t("contracts")}
                data={formatSizePrecision(selectedQuote.amount)}
              />
              <Stat
                key={t("price")}
                title={t("price")}
                data={currency(
                  Math.abs(Number(selectedQuote.limit_price))
                ).format()}
              />
            </StatWrapper>
          ) : null}
        </>
      );
    default:
      return null;
  }
};

interface IBuildConfirmationButtonsParams {
  page: ClosePositionPageEnum;
  canClosePosition: boolean;
  position: GetAccount200ResponsePositionsInner | undefined;
  accountData: AccountData;
  isPerp: boolean;
  closeMode: "market" | "limit";
  executePerpClose: () => void;
  perpCloseValid: boolean;
  onNextPage: () => void;
  onOpenPositionConfirmationModal: () => void;
  onOpenPnLCard: () => void;
  onPrevPage: () => void;
  selectedRfqBlock: GetRfqs200ResponseBlocksInner | undefined;
  selectedQuote: GetRfqsBlockIdQuotes200ResponseAsksInner | undefined;
  loading: boolean;
  onAcceptQuote: () => void;
  onCreate: () => void;
  t: TranslateFn;
}

export const buildConfirmationButtons = ({
  page,
  canClosePosition,
  position,
  accountData,
  isPerp,
  closeMode,
  executePerpClose,
  perpCloseValid,
  onNextPage,
  onOpenPositionConfirmationModal,
  onOpenPnLCard,
  onPrevPage,
  selectedRfqBlock,
  selectedQuote,
  loading,
  onAcceptQuote,
  onCreate,
  t,
}: IBuildConfirmationButtonsParams): IConfirmationButton[] => {
  const buttons: IConfirmationButton[] = [];

  if (page === ClosePositionPageEnum.DETAILS) {
    const canRequestRFQ =
      !canClosePosition &&
      position?.instrument_type === InstrumentTypeResponse.Option;
    if (canRequestRFQ) {
      buttons.push({
        title: t("rfq"),
        onClick: onNextPage,
      });
    } else if (accountData?.in_liquidation) {
      buttons.push({
        overrideComponent: (
          <Button disabled buttonTheme={ButtonThemeEnum.NEGATIVE}>
            {t("liquidations_in_progress")}
          </Button>
        ),
      });
    } else if (isPerp) {
      // Perps execute directly from the inline Market/Limit + size controls.
      buttons.push({
        title:
          closeMode === "limit"
            ? t("place_limit_close", { defaultValue: "Place Limit Close" })
            : t("close_position"),
        onClick: executePerpClose,
        disabled:
          !perpCloseValid || (closeMode === "market" && !canClosePosition),
      });
    } else {
      buttons.push({
        title: t("close_position"),
        onClick: onOpenPositionConfirmationModal,
        disabled: !canClosePosition,
      });
    }

    buttons.push({
      overrideComponent: <ShareButton onClick={onOpenPnLCard} />,
      style: { marginLeft: `${SPACING.two}px` },
    });
  }

  if (page === ClosePositionPageEnum.REQUEST_RFQ) {
    buttons.push({
      overrideComponent: <BackButton onClick={onPrevPage} />,
      style: { marginRight: `${SPACING.two}px` },
    });

    if (selectedRfqBlock) {
      buttons.push({
        title: t("accept_quotation"),
        onClick: () => onAcceptQuote(),
        theme: ButtonThemeEnum.NEUTRAL2,
        disabled: !selectedQuote || loading,
      });
    } else {
      buttons.push({
        title: t("create_rfq"),
        onClick: () => onCreate(),
        theme: ButtonThemeEnum.NEUTRAL2,
        disabled: selectedRfqBlock,
      });
    }
  }

  return buttons;
};
